import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, Row, Col, Select, Typography, Form, Collapse, Checkbox } from 'antd';
import { useHistory } from 'react-router-dom';
import { createFlow } from '../../../lib/api/flow-api';
import { getModelsForAlgorithm } from '../../../lib/api/algorithm-api';
import InvoiceLabels from '../../../config/InvoiceLabels.json';
import _ from 'lodash';
import { UploadFilesStep } from '../upload/UploadFilesStep';
import { Algorithm, Model, Batch } from '../../../types';
import { LabelDefinition } from '../../../types/label';
import { Icon } from '@ant-design/compatible';
import { FormItemLabel, ModalSubTitle, ModalTitle } from '../../../components/text/Titles';

const { Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;
const spinIcon = <Icon type="loading" style={{ fontSize: 16 }} spin />;

type Props = { visible: boolean; onClose: () => any; algorithm: Algorithm };
const CreateFlow = (props: Props) => {
  const { visible, onClose, algorithm } = props;

  let history = useHistory();

  const [step, setStep] = useState<number>(0);
  const [name, setName] = useState<null | string>(null);
  const [models, setModels] = useState<null | Model[]>(null);
  const [model, setModel] = useState<null | Model>(null);
  const [batchUploadReports, setBatchUploadReports] = useState<Batch[]>([]);
  const [isCreating, setCreating] = useState(false);
  const [rules, setRules] = useState<{ [name: string]: string[] }>({});

  useEffect(() => {
    const fetchModels = async () => {
      const models = await getModelsForAlgorithm(algorithm.id);
      const modelsWithTrainings = models.filter(model => model.lastModelVersionId);

      const pretrainedModels = models.filter(model => !model.lastModelVersionId && model.hidden === false);
      pretrainedModels.map(model => (model.pretrained = true));

      setModels([...pretrainedModels, ...modelsWithTrainings]);
    };
    fetchModels();
  }, [setModels, algorithm]);

  const onCreate = async () => {
    if (!name || !model) return;

    setCreating(true);
    const ruleTrans = Object.keys(rules).map(it => ({
      fieldName: it,
      rules: rules[it],
    }));

    const flow = await createFlow(name, model.id, { fields: ruleTrans }, batchUploadReports);
    history.push(`/flows/${algorithm.id}/${flow.id}`);
    onClose();
    setCreating(false);
  };

  const ruleChanged = (field: LabelDefinition) => (checkedValues: string[]) => {
    setRules({ ...rules, [field.name]: checkedValues });
  };

  const renderLabelPanel = (labelDef: LabelDefinition) => {
    return model && model.info.annotationTypes.includes(labelDef.name) ? (
      <Panel
        header={<span>{labelDef.translation}</span>}
        key={labelDef.name}
        style={{
          marginBottom: 8,
          borderRadius: '3px',
          border: 'solid 1px #e8e8e8',
          backgroundColor: '#f7f7f7',
        }}>
        <div style={{ padding: 8 }}>
          <Checkbox.Group
            style={{ width: '100%', marginLeft: '4px' }}
            value={rules[labelDef.name]}
            onChange={ruleChanged(labelDef) as any}>
            <Row>
              {labelDef.rules.map(it => (
                <Col span={22}>
                  <Checkbox value={it.name}>{it.translation}</Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </div>
      </Panel>
    ) : null;
  };

  const next = () => {
    if (step < 3) {
      setStep(step + 1);
    }
  };

  const previous = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const nextButtonIsDisabled = () => {
    if (step === 0) {
      return !name || name.length === 0 || !model;
    } else if (step === 2) {
      return isCreating || !batchUploadReports || batchUploadReports.length === 0;
    }
    return false;
  };

  const previousButton = (
    <Button key="prev" size="large" onClick={previous} disabled={isCreating}>
      Back
    </Button>
  );
  const nextButton = (
    <Button key="submit" type="primary" size="large" onClick={next} disabled={nextButtonIsDisabled()}>
      Next
    </Button>
  );
  const analyzeButton = (
    <Button key="submit" type="primary" size="large" onClick={onCreate} disabled={nextButtonIsDisabled()}>
      {isCreating && spinIcon} Analyze
    </Button>
  );

  return (
    <Modal
      maskClosable={false}
      width={768}
      visible={visible}
      title={<ModalTitle>New Flow</ModalTitle>}
      onOk={onCreate}
      onCancel={onClose}
      footer={step === 0 ? [nextButton] : step === 1 ? [previousButton, nextButton] : [previousButton, analyzeButton]}>
      {step === 0 && (
        <>
          <Text className={'descriptive-title'} style={{ fontSize: '14px' }}>
            This proces will create a new end to end flow. Choose a ML model to analyse your documents, pick the
            validation rules you would like to be applied to the analysis’ results and upload documents you would like
            to process.
          </Text>
          <Row style={{ marginTop: '16px' }}>
            <Col span={24}>
              <Form layout={'vertical'}>
                <Form.Item label="Name">
                  <Input value={name || undefined} onChange={event => setName(event.target.value)} />
                </Form.Item>
                <Form.Item label="Model">
                  <Select
                    onChange={(value: number) => {
                      setModel(models?.find(it => it.id === value) || null);
                    }}>
                    {models &&
                      models.map(it => (
                        <Option key={it.id} value={it.id}>
                          {it.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Form>
            </Col>
          </Row>
        </>
      )}
      {step === 1 && (
        <>
          <FormItemLabel>
            Per label you are able to define rules to shape the outcome or validate the recognised data.
          </FormItemLabel>
          <ModalSubTitle>Define rules per label</ModalSubTitle>
          <Collapse style={{ backgroundColor: 'white' }} bordered={false}>
            {_.sortBy(InvoiceLabels, it => it.translation.toLowerCase()).map(it => renderLabelPanel(it))}
          </Collapse>
        </>
      )}
      {step === 2 && (
        <UploadFilesStep batchUploadReports={batchUploadReports} setBatchUploadReports={setBatchUploadReports} />
      )}
    </Modal>
  );
};

export default CreateFlow;
